import React from 'react';
import { Box, Container, Grid } from '@mui/material';
import styled from 'styled-components';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import WorkIcon from '@mui/icons-material/Work';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import GavelIcon from '@mui/icons-material/Gavel';

const FooterSection = styled(Box)`
  background: linear-gradient(180deg, rgba(1,10,20,0.95) 0%, #010a14 100%);
  color: white;
  padding: 8rem 0;
  position: relative;
  border-top: 1px solid rgba(79, 172, 254, 0.1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, 
      rgba(79, 172, 254, 0) 0%,
      rgba(79, 172, 254, 0.2) 50%,
      rgba(79, 172, 254, 0) 100%
    );
  }
`;

const Logo = styled.h2`
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  letter-spacing: -0.02em;
  background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 100%;
    height: 1px;
    background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%);
    opacity: 0.3;
  }
`;

const Description = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 1rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  max-width: 320px;
  font-family: 'Poppins', sans-serif;
  font-weight: 300;
`;

const FooterHeading = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: #4facfe;
  margin-bottom: 2rem;
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  position: relative;
  padding-bottom: 0.75rem;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 24px;
    height: 2px;
    background: linear-gradient(90deg, #4facfe, rgba(79, 172, 254, 0.2));
  }
`;

const FooterLink = styled.a`
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 300;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  padding-left: 0;
  
  svg {
    font-size: 1.25rem;
    opacity: 0.7;
    transition: all 0.3s ease;
  }
  
  &:hover {
    color: #4facfe;
    padding-left: 8px;
    
    svg {
      color: #4facfe;
      opacity: 1;
    }
  }

  &::before {
    content: '';
    position: absolute;
    left: 24px;
    top: 50%;
    width: 0;
    height: 1px;
    background: linear-gradient(90deg, #4facfe, transparent);
    transition: all 0.3s ease;
    opacity: 0;
  }

  &:hover::before {
    width: 24px;
    opacity: 0.5;
  }
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 1.25rem;
  margin-top: 1.5rem;

  a {
    color: rgba(255, 255, 255, 0.7);
    transition: all 0.3s ease;
    padding: 10px;
    background: rgba(79, 172, 254, 0.05);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(79, 172, 254, 0.1);
    
    &:hover {
      color: #4facfe;
      background: rgba(79, 172, 254, 0.1);
      transform: translateY(-2px);
      border-color: rgba(79, 172, 254, 0.2);
    }

    svg {
      font-size: 1.25rem;
    }
  }
`;

const CopyrightSection = styled.div`
  border-top: 1px solid rgba(79, 172, 254, 0.1);
  margin-top: 6rem;
  padding-top: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.875rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 300;

  .company-name {
    background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 600;
  }

  div {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  img {
    height: 16px;
    margin-left: 0.25rem;
    opacity: 0.7;
    transition: opacity 0.3s ease;

    &:hover {
      opacity: 1;
    }
  }
`;

const FooterContainer = styled(Container)`
  position: relative;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: -150px;
    right: -200px;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(79, 172, 254, 0.03) 0%, rgba(79, 172, 254, 0) 70%);
    border-radius: 50%;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -100px;
    left: -150px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(0, 242, 254, 0.02) 0%, rgba(0, 242, 254, 0) 70%);
    border-radius: 50%;
    pointer-events: none;
  }
`;

interface FooterProps {}

const Footer: React.FC<FooterProps> = () => {
  return (
    <FooterSection>
      <FooterContainer maxWidth="lg">
        <Grid container spacing={8}>
          <Grid item xs={12} md={4}>
            <Logo>About</Logo>
            <Description>
              Transforming businesses through innovative AI and data science solutions.
            </Description>
            <SocialIcons>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer">
                <LinkedInIcon />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer">
                <TwitterIcon />
              </a>
            </SocialIcons>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <FooterHeading>
              COMPANY
            </FooterHeading>
            <FooterLink href="/career">
              <WorkIcon />
              Careers
            </FooterLink>
            <FooterLink href="#news">
              <NewspaperIcon />
              News
            </FooterLink>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <FooterHeading>
              LEGAL
            </FooterHeading>
            <FooterLink href="/privacy-policy">
              <PrivacyTipIcon />
              Privacy Policy
            </FooterLink>
            <FooterLink href="/terms-of-service">
              <GavelIcon />
              Terms Of Service
            </FooterLink>
          </Grid>
        </Grid>

        <CopyrightSection>
          <div>Copyright © 2025 Yurix Ventures Private Limited.</div>
          <div>
            Made in India
            <img 
              src="https://upload.wikimedia.org/wikipedia/en/thumb/4/41/Flag_of_India.svg/1200px-Flag_of_India.svg.png" 
              alt="Indian Flag"
            />
          </div>
        </CopyrightSection>
      </FooterContainer>
    </FooterSection>
  );
};

export default Footer; 